import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import { Clipboard } from 'ts-clipboard'
import { Button } from '@giphy/react-giphy-brand'
import { IGif } from '@giphy/js-types'
import MessageContext from 'shared/components/context/message'
import { getEmbedCode } from 'shared/util/gif-embed'
import ToggleButton from 'shared/components/toggle-button'
import { trackEvent } from 'shared/util/gif-detail-ga-events'

const StyledToggle = styled(ToggleButton)`
    background: #000;
`

const Container = styled.div`
    width: 100%;
    height: 100%;
    padding: 30px;
`

const Subheader = styled.h3`
    font-weight: bold;
    font-size: 15px;
    margin: 20px 0 10px 0;
`

const ExplanationText = styled.p`
    font-size: 15px;
    line-height: 19px;
`

const ToggleContainer = styled.div`
    width: 200px;

    *:first-child {
        // force child to have 0 margin
        margin: 0 !important;
    }
`

const EmbedCodeContainer = styled.div`
    width: calc(100% - 150px);
    background-color: white;
    padding: 11px;
    overflow: hidden;
    color: black;
    font-size: 14px;
    white-space: nowrap;
    position: relative;

    &:after {
        content: '';
        width: 65px;
        height: 100%;
        right: 0;
        position: absolute;
        background-image: linear-gradient(to right, transparent, white);
    }
`

const EmbedControls = styled.div`
    display: flex;
    flex-direction: row;
`

const CopyCodeButton = styled(Button)`
    height: 40px;
    font-size: 16px;
    background: linear-gradient(45deg, rgb(153, 51, 255) 0%, rgb(97, 87, 255) 100%);
    width: 150px;
`

type Props = {
    gif: IGif
}

const EmbedPane = ({ gif }: Props) => {
    const [isResponsive, setIsResponsive] = useState<boolean>(false)
    const [isCodeCopied, setIsCodeCopied] = useState<boolean>(false)
    const { sendMessage } = useContext(MessageContext)
    const embedCode = getEmbedCode(gif, { video: 1 }, isResponsive)
    const onOptionsChange = () => {
        setIsResponsive(!isResponsive)
        setIsCodeCopied(false)
    }

    const copyCode = (text: string) => {
        Clipboard.copy(text)
        setIsCodeCopied(true)
        sendMessage('The Embed Code has been copied to your clipboard!', 'success')
    }

    return (
        <Container>
            <ExplanationText>
                Want to embed this GIF on your website or blog?
                <br />
                Just drop in the embed code below and you&apos;re done!
            </ExplanationText>
            <Subheader>Responsive</Subheader>
            <ToggleContainer>
                <StyledToggle
                    onText={'On'}
                    offText={'Off'}
                    isOn={isResponsive}
                    width={200}
                    height={32}
                    toggle={() => onOptionsChange()}
                    background={'#000'}
                />
            </ToggleContainer>
            <Subheader>Embed Code</Subheader>
            <EmbedControls>
                <EmbedCodeContainer>{embedCode}</EmbedCodeContainer>
                <CopyCodeButton
                    onClick={() => {
                        copyCode(embedCode)
                        trackEvent('Embed Click', `${gif.id}`)
                    }}
                >
                    {isCodeCopied ? 'Copied!' : 'Copy Code'}
                </CopyCodeButton>
            </EmbedControls>
        </Container>
    )
}

export default EmbedPane
